import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../../components/CustomDataGridV2";
import ActionMenu from "../../../../../../../../components/app/ActionMenu";
import DeleteModal, {
  useDeleteDisClosure,
} from "../../../../../../../../components/app/DeleteModal";
import { useRoleController } from "../../../../../../../../components/useRoleController";
import useDisclosure from "../../../../../../../../hooks/use-disclosure";
import { useArchiveVaccine } from "../../../../../../../../queries/vaccines/use-patient-vaccine-archive";
import { usePatientVaccinesList } from "../../../../../../../../queries/vaccines/use-patient-vaccines";
import { PatientVaccine } from "../../../../../../../../services";
import { formatDate } from "../../../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  filterActionCol,
  getXtenantId,
  replaceUnderScoreAddSpace,
  roleFilter,
} from "../../../../../../../../utils/utils";
import UpsertVaccineDrawer from "../../vaccines/UpsertVaccineDrawer";
import { getVaccineEditFormattedPayload } from "../../vaccines/vaccine-constant";
import {
  VACCINE_ROUTE_MAPPER,
  VACCINE_SITE_MAPPER,
} from "./ClinicalLayout-constant";

interface VaccinesListProps {
  isInsideVisitNote?: boolean;
}

const VaccinesList = ({ isInsideVisitNote = false }: VaccinesListProps) => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const { patientId } = useParams() as { patientId: any };
  const { mutateAsync: archiveVaccine } = useArchiveVaccine();
  const vaccinesDisclosure = useDisclosure(false);

  const { data: vaccinesList, refetch } = usePatientVaccinesList({
    patientUuid: patientId,
    pagination,
    gridSorting,
    archive: false,
    xTENANTID: getXtenantId() ?? undefined,
  });

  const deleteVaccinesDisclosure = useDeleteDisClosure();
  const { hasDelete, hasUpdate } = useRoleController("Vaccine");
  const {
    hasDelete: hasClinicalDelete,
    hasUpdate: hasClinicalUpdate,
    hasReadOnly,
  } = useRoleController("Clinical");

  const COLUMNS = applyDefaultColumnSettings<PatientVaccine>(
    [
      {
        field: "vaccine",
        headerName: "Vaccine Name",
        flex: 0.4,
        minWidth: 120,
        renderCell: ({ value }) => value?.name || "-",
      },
      // {
      //   field: "doseDuration",
      //   headerName: "Dose Duration",
      //   flex: 0.3,
      //   minWidth: 110,
      //   renderCell: ({ value }) => value || "-",
      // },
      {
        field: "administer_date",
        headerName: "Administer Date",
        flex: 0.35,
        minWidth: 110,
        renderCell: ({ row }) =>
          row?.administerDate ? formatDate(row?.administerDate) : "-",
      },
      {
        field: "administeredBy",
        headerName: "Administered By",
        flex: 0.35,
        minWidth: 120,
        renderCell: ({ value }) =>
          Object?.keys(value ?? {})?.length > 0
            ? (Object.values(value)?.[0] as any)
            : "-",
      },
      {
        field: "expiry_date",
        headerName: "Expiry Date",
        flex: 0.3,
        minWidth: 90,
        renderCell: ({ row }) =>
          row?.expiryDate ? formatDate(row?.expiryDate) : "-",
      },
      {
        field: "dose",
        headerName: "Amount",
        flex: 0.25,
        minWidth: 80,
        renderCell: ({ row }) =>
          row?.dose
            ? `${row?.dose} ${
                row?.units
                  ? replaceUnderScoreAddSpace(row?.units?.toLocaleLowerCase())
                  : ""
              }`
            : "-",
      },
      {
        field: "route",
        headerName: "Route",
        flex: 0.2,
        minWidth: 70,
        renderCell: ({ value }) => VACCINE_ROUTE_MAPPER[value] ?? "-",
      },
      {
        field: "site",
        headerName: "Site",
        flex: 0.2,
        minWidth: 60,
        renderCell: ({ value }) => VACCINE_SITE_MAPPER[value] ?? "-",
      },
      // {
      //   field: "manufacturer",
      //   headerName: "Manufacturer",
      //   flex: 0.4,
      //   minWidth: 110,
      //   renderCell: ({ value }) => value ?? "-",
      // },
      // {
      //   field: "lot",
      //   headerName: "Lot Number",
      //   flex: 0.4,
      //   minWidth: 110,
      //   renderCell: ({ value }) => value ?? "-",
      // },
      {
        field: "Action",
        headerName: "Actions",
        width: 70,
        align: "center",
        headerAlign: "center",
        hideSortIcons: true,
        renderCell: ({ row }) => {
          return (
            <ActionMenu
              options={roleFilter({
                list: [
                  { value: "edit", label: "Edit" },
                  { value: "delete", label: "Delete" },
                ],
                compWith: {
                  edit: hasUpdate || hasClinicalUpdate,
                  delete: hasDelete || hasClinicalDelete,
                },
              })}
              onActionClick={(v) => {
                if (v === "edit") {
                  vaccinesDisclosure.open(getVaccineEditFormattedPayload(row));
                } else deleteVaccinesDisclosure.open(row?.uuid);
              }}
            />
          );
        },
      },
    ],
    ["administer_date", "expiry_date", "dose"]
  );

  function archiveHandler<T>(value: boolean, id: T) {
    return archiveVaccine({
      patientVaccineId: id?.toString() ?? "",
      status: value,
      xTENANTID: getXtenantId() ?? undefined,
    }).then((res) => {
      refetch();
      return res;
    });
  }

  return (
    <>
      <UpsertVaccineDrawer disclosureHost={vaccinesDisclosure} />

      <DeleteModal
        w="380px"
        open={deleteVaccinesDisclosure.opened}
        onClose={deleteVaccinesDisclosure.close}
        id={deleteVaccinesDisclosure.deleteId}
        onConfirmation={(id) => archiveHandler(true, id)}
        content={"You want to delete this Vaccine"}
      />

      {/* <ChartTableWrapper> */}
      <CustomDataGridV2
        getRowId={(row) => row?.uuid}
        rows={vaccinesList?.data?.content ?? []}
        rowCount={vaccinesList?.data?.page?.totalElements ?? 10}
        columns={filterActionCol(COLUMNS, hasReadOnly || isInsideVisitNote)}
        // sx={{ marginTop: "40px" }}
        disableRowSelectionOnClick
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        onSortModelChange={setGridSorting}
      />
      {/* </ChartTableWrapper> */}
    </>
  );
};

export default VaccinesList;
